import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Container, Loader2, ShieldCheck } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await login(email.trim().toLowerCase(), password);
    setLoading(false);
    if (res.ok) {
      toast.success("Access granted — welcome to ONLOGIST PRO");
      navigate("/");
    } else {
      toast.error(res.error);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-950 relative overflow-hidden px-4">
      <div
        className="absolute inset-0 opacity-[0.15]"
        style={{
          backgroundImage:
            "linear-gradient(#1e293b 1px, transparent 1px), linear-gradient(90deg, #1e293b 1px, transparent 1px)",
          backgroundSize: "44px 44px",
        }}
      />
      <div className="absolute -top-24 -right-24 h-72 w-72 rounded-full bg-orange-600/20 blur-3xl" />

      <div className="relative w-full max-w-md bg-slate-900/90 border border-slate-700 rounded-lg p-8 shadow-2xl backdrop-blur-md" data-testid="login-card">
        <div className="flex items-center gap-3 mb-6">
          <div className="h-11 w-11 rounded-md bg-orange-600 flex items-center justify-center shadow-lg shadow-orange-900/40">
            <Container className="h-6 w-6 text-white" />
          </div>
          <div>
            <div className="font-heading font-extrabold tracking-wide text-2xl uppercase leading-none">
              Onlogist<span className="text-orange-500"> Pro</span>
            </div>
            <div className="font-mono text-[10px] uppercase tracking-wider text-slate-500 mt-1">
              Manufacturing & Warehouse OS
            </div>
          </div>
        </div>

        <h1 className="text-slate-100 text-lg font-semibold mb-1">Secure sign in</h1>
        <p className="text-sm text-slate-400 mb-6">Authenticate to access the command center.</p>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="font-mono text-xs uppercase tracking-wider text-slate-400">Email</label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="login-email-input"
              className="mt-1.5 w-full rounded-md bg-slate-950 border border-slate-700 px-3 py-2.5 text-sm text-slate-100 outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500 transition-colors"
              placeholder="you@company.com"
            />
          </div>
          <div>
            <label className="font-mono text-xs uppercase tracking-wider text-slate-400">Password</label>
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="login-password-input"
              className="mt-1.5 w-full rounded-md bg-slate-950 border border-slate-700 px-3 py-2.5 text-sm text-slate-100 outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500 transition-colors"
              placeholder="••••••••"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            data-testid="login-submit-button"
            className="w-full flex items-center justify-center gap-2 bg-orange-600 hover:bg-orange-500 disabled:opacity-60 text-white font-semibold px-4 py-2.5 rounded-md shadow-md transition-all active:scale-[0.98]"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
            {loading ? "Authenticating..." : "Sign in"}
          </button>
        </form>

        <div className="mt-5 pt-5 border-t border-slate-800 text-center space-y-2">
          <div className="text-sm text-slate-400">
            New to ONLOGIST PRO?{" "}
            <a href="/signup" data-testid="login-signup-link" className="text-orange-400 hover:text-orange-300 font-semibold transition-colors">
              Create a company account
            </a>
          </div>
          <a href="/forgot-password" data-testid="login-forgot-link" className="inline-block text-xs text-slate-500 hover:text-orange-400 transition-colors">
            Forgot password?
          </a>
        </div>
      </div>
    </div>
  );
}
